/**
 * ScoreIndicator Component - Visual Score Display with Dots
 * @module features/results/components/ScoreIndicator
 *
 * Displays a score as visual dots (e.g., ●●●●○○).
 * Color-coded based on score percentage:
 * - High (>66%): green
 * - Medium (33-66%): yellow
 * - Low (<33%): red
 */

import { cn } from '@/lib/utils';

export interface ScoreIndicatorProps {
  /** Score value from 0 to 100 */
  score: number;
  /** Maximum number of dots to display (default: 6) */
  maxScore?: number;
  /** Size of the dots (default: 'sm') */
  size?: 'sm' | 'md';
}

/**
 * ScoreIndicator displays a score as visual dots
 *
 * @example
 * ```tsx
 * <ScoreIndicator score={72} />
 * // Renders: ●●●●○○ (green, 4 filled of 6)
 * ```
 */
export function ScoreIndicator({ score, maxScore = 6, size = 'sm' }: ScoreIndicatorProps) {
  // Calculate percentage and number of filled dots
  const percentage = Math.min(100, Math.max(0, score)) / 100;
  const filledCount = Math.round(percentage * maxScore);

  // Determine color based on percentage
  const getColorClass = () => {
    if (percentage > 0.66) return 'text-green-500';
    if (percentage >= 0.33) return 'text-yellow-500';
    return 'text-red-500';
  };

  const sizeClass = size === 'md' ? 'text-base' : 'text-sm';
  const colorClass = getColorClass();

  return (
    <span
      role="img"
      aria-label={`Score: ${score} de 100`}
      className={cn('inline-flex gap-0.5', colorClass)}
    >
      {Array.from({ length: maxScore }, (_, index) => (
        <span
          key={index}
          data-testid={index < filledCount ? 'dot-filled' : 'dot-empty'}
          className={cn(
            sizeClass,
            'select-none',
            index < filledCount ? 'opacity-100' : 'opacity-30'
          )}
        >
          ●
        </span>
      ))}
    </span>
  );
}

export default ScoreIndicator;
